import { useEffect, useState, useCallback } from 'react';
import {
  Link,
  NavLink,
  Outlet,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { changeLanguage } from 'i18next';
import { ampli } from '../../ampli';

import { useCurrentUser } from '../../contexts/CurrentUser';
import { useCurrentQuota } from '../../contexts/CurrentQuota';
import { API } from '../../API';
import { getLoadedSudo, useSudoStale } from '../../utils/sudoState';
import { useSudoWorkspaceNames } from '../../components/WorkspaceChangeGuard/WorkspaceChangeGuard';
import { showRequestError } from '../../utils/requestError';
import { useAuth } from '../../contexts/Auth';
import { Loader } from '../../components/Loader/Loader';
import { ReactComponent as ReportsIcon } from './assets/report.svg';
import { ReactComponent as NarrativesIcon } from './assets/narratives.svg';
import { ReactComponent as CloseIcon } from './assets/close.svg';
import { ReactComponent as SourcesIcon } from './assets/sources.svg';
import { ReactComponent as LogoutIcon } from './assets/logout.svg';
import { ReactComponent as GroupsIcon } from './assets/database.svg';
import { ReactComponent as SearchIcon } from './assets/search.svg';
import { ReactComponent as UserStatsIcon } from './assets/userStats.svg';
import { ReactComponent as WorkspacesIcon } from './assets/workspaces.svg';
import { ReactComponent as UserIcon } from './assets/user.svg';
import { ReactComponent as DashboardsIcon } from './assets/dashboard.svg';
import { ReactComponent as ExportIcon } from './assets/export.svg';
import { ReactComponent as TopicIcon } from './assets/topicDiscovery.svg';
import { ReactComponent as OsavulIcon } from '../../assets/osavulLogo.svg';
import { ReactComponent as OsavulFullIcon } from '../../assets/osavul-full.svg';
import { ReactComponent as ChevronIcon } from '../../pages/SearchPage/assets/chevron-down.svg';
import { ReactComponent as SettingsIcon } from './assets/settings.svg';
import { ReactComponent as HelpIcon } from './assets/help.svg';
import { ReactComponent as LinkIcon } from '../../assets/link.svg';
import { ReactComponent as CustomDashboardIcon } from './assets/customDashboard.svg';
import { ReactComponent as ArtifactsIcon } from './assets/artifacts.svg';
import { ReactComponent as WindmillIcon } from './assets/windmill.svg';
import { ReactComponent as SocialGraphIcon } from './assets/socialGraph.svg';
import { DropdownMenu } from '../../components/DropdownMenu/DropdownMenu';
import { ButtonsList } from '../../components/DropdownMenu/ButtonsList';

import websocketConnection from '../../websocket';
import { ThreatsLink } from '../ThreatsPage/ThreatsLink';
import { JanusLink } from "../../components/Janus/JanusLink";
import { NarrativesModerationLink } from '../NarrativesModerationPage/NarrativesModerationLink';
import Tooltip from '../../components/Tooltip/Tooltip';
import menuIcon from './assets/menu.svg';
import { ToastContainer, toast } from 'react-toastify';
import { ReloadNavLink } from './ReloadNavLink';
import { MaintenanceMessage } from './MaintenanceMessage';
import { Mandatory2FAModal } from '../../components/Mandatory2FAModal/Mandatory2FAModal';
import { LoginPage2FASetup } from '../../components/LoginPage2FASetup/LoginPage2FASetup';
import { TWO_FA_MANDATORY_DATE } from '../../constants/twoFactorAuth';
import { WhatsNew } from '../../components/WhatsNew/WhatsNew';
import { CopilotButton } from '../../components/CopilotSwitch/CopilotSwitch';
import { useVersionCheck } from '../../hooks/useVersionCheck';
import { getWorkspaceRoles } from '../../utils/artifactsUi';
import 'react-toastify/dist/ReactToastify.css';

import styles from './Layout.module.scss';

export function Layout() {
  const { t } = useTranslation();
  const [currentUser, setCurrentUser] = useCurrentUser();
  const [currentQuota, setCurrentQuota] = useCurrentQuota();
  const [websiteConfig, setWebsiteConfig] = useState(null);
  const workspaceConfig = currentUser?.workspace?.config;
  const featureFlags = workspaceConfig?.featureflags;
  const [maintenanceMessage, setMaintenanceMessage] = useState(null);
  const [isNavToogleChecked, setisNavToogleChecked] = useState(true);
  const [isAiQuotaLow, setIsAiQuotaLow] = useState(false);
  const [isAnalyticalQuotaLow, setIsAnalyticalQuotaLow] = useState(false);
  const [isAIQuotaInsufficient, setIsAIQuotaInsufficient] = useState(false);
  const [isAnalyticalQuotaInsufficient, setIsAnalyticalQuotaInsufficient] = useState(false);
  const [isTrialEnding, setIsTrialEnding] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [show2FABanner, setShow2FABanner] = useState(false);
  // Tracks whether the authoritative user record has loaded from /users/me.
  // Until it has, `currentUser` may be a stale/empty object (e.g. `{}` set on
  // logout) whose `otp_2fa_enabled` is undefined, which must not trigger the
  // mandatory-2FA setup screen for a user who already has 2FA enabled.
  const [isUserLoaded, setIsUserLoaded] = useState(false);
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasNewVersion, currentVersion, lastSeenVersion, dismissNewVersion, applyNewVersion } = useVersionCheck();

  const calculateTrialDaysLeft = () => {
    if (currentUser?.workspace?.status !== 'TRIAL') {
      return 0;
    }
    const endDate = new Date(currentUser?.workspace?.trial_end_date);
    const currentDate = new Date();
    const diffTime = endDate - currentDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const logoutHandler = async () => {
    await auth.signOut();
    setCurrentUser({});
    navigate('/');
  };

  const sudoGetOutHandle = () => {
    localStorage.removeItem('workspace_id');
    localStorage.removeItem('workspace_name');
    // Full reload, mirroring WorkspacesPage.handleSudo: an in-app navigate
    // leaves currentUser/currentQuota frozen on the sudo workspace, and it
    // leaves WorkspaceChangeGuard believing this tab still runs under sudo.
    window.location.href = '/workspaces';
  };

  const getInitials = () => {
    const firstLatter = currentUser.first_name ? currentUser.first_name[0] : '';
    const lastLatter = currentUser.last_name ? currentUser.last_name[0] : '';

    return `${firstLatter}${lastLatter}`;
  };

  const getWorkspaceQuota = useCallback(() => {
    API.fetch('GET', `/API/v1/workspaces/current/quota`).then((data) => {
      setCurrentQuota(data);
      if (data.quota?.AI?.limit > 0 && data.quota?.AI?.is_low) {
        if (data.quota?.AI?.balance > 0) {
          setIsAiQuotaLow(true);
        } else {
          setIsAIQuotaInsufficient(true);
        }
      }

      if (data.quota?.ANALYTICAL?.is_low) {
        if (data.quota?.ANALYTICAL?.balance > 0) {
          setIsAnalyticalQuotaLow(true);
        } else {
          setIsAnalyticalQuotaInsufficient(true);
        }
      }

    }).catch((error) => showRequestError(error, t('Workspace quota')));
  }, [currentUser, t]);

  useEffect(() => {
    getWorkspaceQuota();
  }, [getWorkspaceQuota]);

  useEffect(() => {
    API.fetch('GET', '/API/v1/users/me').then((data) => {
      setCurrentUser(data);
      setIsUserLoaded(true);

      if (
        data?.workspace?.status === 'TRIAL' &&
        data?.workspace?.is_trial_ending
      ) {
        setIsTrialEnding(true);
      }

      if (!data?.otp_2fa_enabled && new Date() < TWO_FA_MANDATORY_DATE) {
        if (localStorage.getItem('2fa_modal_dismissed')) {
          setShow2FABanner(true);
        } else {
          setShow2FAModal(true);
        }
      }

      if (data.language === null) {
        return;
      }

      changeLanguage(data.language.toLowerCase());
    });

    // should be fetched only on init
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      localStorage.getItem('i18nextLng') === 'ua-UK' ||
      localStorage.getItem('i18nextLng') === 'ru-RU' ||
      localStorage.getItem('i18nextLng') === 'ru' ||
      localStorage.getItem('i18nextLng') === 'uk'
    ) {
      setMaintenanceMessage(websiteConfig?.maintenance_message_uk);
    } else {
      setMaintenanceMessage(websiteConfig?.maintenance_message_en);
    }
  }, [websiteConfig]);

  useEffect(() => {
    API.fetch('GET', '/API/v1/config/website_config').then((websiteConfig) => {
      setWebsiteConfig(websiteConfig);
    });
  }, []);

  useEffect(() => {
    const notifyExportProgress = (notification) => {
      if (notification?.object_type !== 'ASYNC_EXPORT') {
        return;
      }

      let data = notification?.data;

      const Msg = () => (
        <div>
          {t('An export of') +
            ` ${t('export_type_of_' + data.type)} id=${data.object_id} `}
          <Link to={`/exports?object_id=${data.object_id}&types=${data.type}`}>
            {t(`export_status_${data.status}`).toLowerCase()}
          </Link>
        </div>
      );

      const displayMsg = () => {
        toast.success(<Msg />);
      };
      displayMsg();
    };

    websocketConnection.addHandler('ASYNC_EXPORT', notifyExportProgress);

    return () => {
      websocketConnection.removeHandler('ASYNC_EXPORT');
    };
  }, []);

  // Graph generation outcome toasts ("you can leave this page, we'll notify
  // you"). The backend pushes SOCIAL_GRAPH messages only to the graph's creator
  // (personal channel), so every message received here is for the current user.
  // Other components (GraphsPage, the graph widget) listen to the same type for
  // their own UI — cleanup must remove exactly this handler, not all of them.
  useEffect(() => {
    const notifySocialGraphStatus = (notification) => {
      const data = notification?.data;
      if (!data?.id) {
        return;
      }

      if (data.status === 'ready') {
        toast.success(
          <div>
            {t('Graph "{{name}}" is ready.', { name: data.name })}{' '}
            <Link to={`/graphs/${data.id}`}>{t('Open graph')}</Link>
          </div>,
        );
      } else if (data.status === 'failed') {
        toast.error(t('Graph "{{name}}" failed to generate.', { name: data.name }));
      }
    };

    websocketConnection.addHandler('SOCIAL_GRAPH', notifySocialGraphStatus);

    return () => {
      websocketConnection.removeHandler('SOCIAL_GRAPH', notifySocialGraphStatus);
    };
  }, [t]);

  // The snapshot, not a live localStorage read: another tab can clear or
  // change the key at any time, and the banner must keep describing the
  // workspace this page actually rendered.
  const workspaceName = getLoadedSudo().name;
  const sudoStale = useSudoStale();
  // While stale, the sudo banner describes the workspace the user is actually
  // in now (the one another tab switched to), not the one this page rendered.
  // Keep the banner up even when that tab went back to Main (name null), so
  // the stale page still shows it is read-only and offers "Get out".
  const { shared: sharedSudo } = useSudoWorkspaceNames();
  const bannerWorkspaceName = sudoStale ? (sharedSudo.name ?? t('Main workspace')) : workspaceName;
  const workspaceRoles = getWorkspaceRoles(currentUser);

  const quotaLink = [
    <Link to="/users/settings#billing" target='_blank' rel='noreferrer'>
      {t('Check subscription')}
    </Link>,
  ];
  
  if (!currentUser) {
    return <Loader />;
  }

  let menuItems = [
    {
      show: workspaceRoles?.includes('menu:janus'),
      content: (
        <li key={'menu:janus'}>
          <JanusLink
            itemTextClassname={styles.itemText}
            threatCounterClassname={styles.threatCounter}
          />
        </li>
      ),
    },
    {
      show: workspaceRoles?.includes('menu:search'),
      content: (
        <li key={'menu:search'}>
          <ReloadNavLink to="/search">
            <Tooltip content={t('Search')} position="right">
              <SearchIcon />
            </Tooltip>
            <span className={styles.itemText}>{t('Search')}</span>
          </ReloadNavLink>
        </li>
      ),
    },
    {
      show: workspaceRoles?.includes('menu:narratives'),
      content: (
        <li key={'menu:narratives'}>
          <ReloadNavLink to="/narratives">
            <Tooltip content={t('Cases')} position="right">
              <NarrativesIcon />
            </Tooltip>
            <span className={styles.itemText}>{t('Cases')}</span>
          </ReloadNavLink>
        </li>
      ),
    },
    {
      show:
        workspaceRoles?.includes('menu:narratives') &&
        currentUser.type === 'MODERATOR',
      content: (
        <li key={'menu:narratives_moderation'}>
          <NarrativesModerationLink
            itemTextClassname={styles.itemText}
            threatCounterClassname={styles.threatCounter}
          />
        </li>
      ),
    },
    {
      show: workspaceRoles?.includes('menu:reports'),
      content: (
        <li key={'menu:reports'}>
          <ReloadNavLink to="/reports">
            <Tooltip content={t('Reports')} position="right">
              <ReportsIcon />
            </Tooltip>
            <span className={styles.itemText}>{t('Reports')}</span>
          </ReloadNavLink>
        </li>
      ),
    },
    {
      show: workspaceRoles?.includes('menu:topic_reports'),
      content: (
        <li key={'menu:topic_reports'}>
          <NavLink to="/topic_reports">
            <Tooltip content={t('Topic discovery')} position="right">
              <TopicIcon />
            </Tooltip>
            <span className={styles.itemText}>{t('Topic discovery')}</span>
          </NavLink>
        </li>
      ),
    },
    {
      show: workspaceRoles?.includes('menu:sources'),
      content: (
        <li key={'menu:sources'}>
          <ReloadNavLink to="/sources">
            <Tooltip content={t('Actors')} position="right">
              <SourcesIcon />
            </Tooltip>
            <span className={styles.itemText}>{t('Actors')}</span>
          </ReloadNavLink>
        </li>
      ),
    },
    {
      show: workspaceRoles?.includes('menu:source_groups'),
      content: (
        <li key={'menu:source_groups'}>
          <ReloadNavLink to="/groups">
            <Tooltip content={t('Actor groups')} position="right">
              <GroupsIcon />
            </Tooltip>
            <span className={styles.itemText}>{t('Actor groups')}</span>
          </ReloadNavLink>
        </li>
      ),
    },
    {
      show: workspaceRoles?.includes('menu:threats'),
      content: (
        <li key={'menu:threats'}>
          <ThreatsLink
            itemTextClassname={styles.itemText}
            threatCounterClassname={styles.threatCounter}
          />
        </li>
      ),
    },
    {
      show: workspaceRoles?.includes('menu:dashboards'),
      content: (
        <li key={'menu:dashboards'}>
          <NavLink to="/dashboards">
            <Tooltip content={t('Dashboards')} position="right">
              <DashboardsIcon />
            </Tooltip>
            <span className={styles.itemText}>{t('Dashboards')}</span>
          </NavLink>
        </li>
      ),
    },
    {
      show: workspaceRoles?.includes('menu:artifacts'),
      content: (
        <li key={'menu:artifacts'}>
          <NavLink to="/artifacts">
            <Tooltip content={t('Artifacts')} position="right">
              <ArtifactsIcon />
            </Tooltip>
            <span className={styles.itemText}>{t('Artifacts')}</span>
          </NavLink>
        </li>
      ),
    },
    {
      show: workspaceRoles?.includes('menu:custom_dashboards'),
      content: (
        <li key={'menu:custom-dashboards'}>
          <NavLink to="/custom-dashboards">
            <Tooltip content={t('Custom Dashboards')} position="right">
              <CustomDashboardIcon />
            </Tooltip>
            <span className={styles.itemText}>{t('Custom Dashboards')}</span>
          </NavLink>
        </li>
      ),
    },
    {
      show: workspaceRoles?.includes('menu:user_stats'),
      content: (
        <li key={'menu:user_stats'}>
          <ReloadNavLink to="/user_stats">
            <Tooltip content={t('User stats')} position="right">
              <UserStatsIcon />
            </Tooltip>
            <span className={styles.itemText}>{t('User stats')}</span>
          </ReloadNavLink>
        </li>
      ),
    },
    {
      show: workspaceRoles?.includes('menu:workspaces'),
      content: (
        <li key={'menu:workspaces'}>
          <ReloadNavLink to="/workspaces">
            <Tooltip content={t('Workspaces')} position="right">
              <WorkspacesIcon />
            </Tooltip>
            <span className={styles.itemText}>{t('Workspaces')}</span>
          </ReloadNavLink>
        </li>
      ),
    },
    {
      show: workspaceRoles?.includes('menu:async_export'),
      content: (
        <li key={'menu:async_export'}>
          <ReloadNavLink to="/exports">
            <Tooltip content={t('Exports')} position="right">
              <ExportIcon />
            </Tooltip>
            <span className={styles.itemText}>{t('Exports')}</span>
          </ReloadNavLink>
        </li>
      ),
    },
    {
      show: currentUser.is_admin,
      content: (
        <li key={'menu:users'}>
          <ReloadNavLink to="/users">
            <Tooltip content={t('Users')} position="right">
              <UserIcon />
            </Tooltip>
            <span className={styles.itemText}>{t('Users')}</span>
          </ReloadNavLink>
        </li>
      ),
    },
    {
      show: workspaceRoles?.includes('menu:windmill_apps'),
      content: (
        <li key={'menu:windmill_apps'}>
          <ReloadNavLink to="/windmill-apps">
            <Tooltip content={t('Windmill Apps')} position="right">
              <WindmillIcon />
            </Tooltip>
            <span className={styles.itemText}>{t('Windmill Apps')}</span>
          </ReloadNavLink>
        </li>
      ),
    },
    {
      show: featureFlags?.includes('social_graph'),
      content: (
        <li key={'menu:graphs'}>
          <NavLink to="/graphs">
            <Tooltip content={t('Graphs')} position="right">
              <SocialGraphIcon />
            </Tooltip>
            <span className={styles.itemText}>{t('Graphs')}</span>
          </NavLink>
        </li>
      ),
    },
  ];

  if (currentUser?.workspace?.config?.is_reporter_only_user) {
    menuItems = [
      {
        show: workspaceRoles?.includes('menu:threats'),
        content: (
          <li key={'menu:threats'}>
            <ThreatsLink
              itemTextClassname={styles.itemText}
              threatCounterClassname={styles.threatCounter}
            />
          </li>
        ),
      },
    ];
  }

  if (
    isUserLoaded &&
    currentUser &&
    !currentUser.otp_2fa_enabled &&
    currentUser.workspace?.config?.two_factor_required &&
    new Date() >= TWO_FA_MANDATORY_DATE
  ) {
    return <LoginPage2FASetup onComplete={() => {
      API.fetch('GET', '/API/v1/users/me')
        .then((data) => {
          setCurrentUser(data);
        })
        .catch((error) => showRequestError(error, t('User')));
    }} />;
  }

  return (
    <div>
      <input
        type="checkbox"
        id="nav-toggle"
        checked={isNavToogleChecked}
        onChange={() => setisNavToogleChecked(!isNavToogleChecked)}
      />

      <div className="sidebar">
        <div className="sidebar-brand">
          <h1>
            <span>
              <OsavulIcon className="short" style={{ width: '35px' }} />
              <OsavulFullIcon className="full" style={{ width: '100px' }} />
            </span>
          </h1>
        </div>

        <div className="sidebar-menu">
          <ul>
            {menuItems
              .filter((menuItem) => menuItem.show)
              .map((menuItem) => menuItem.content)}
          </ul>
          <div className={styles.userSettings}>
            <WhatsNew
              hasNewVersion={hasNewVersion}
              currentVersion={currentVersion}
              lastSeenVersion={lastSeenVersion}
              onDismiss={dismissNewVersion}
              onApply={applyNewVersion}
            />
            <button
              type="button"
              className={styles.logoutBtn}
              onClick={(() => {
                window.open('https://docs.osavul.cloud/', '_blank', 'noopener,noreferrer');
                ampli.track({
                  event_type: 'Open User Documentation',
                  event_properties: {
                    user_id: currentUser?.id,
                    workspace_id: currentUser?.workspace_id,
                  }
                })
              })}
            >
              <Tooltip content={t('Docs')} position="right">
                <HelpIcon />
              </Tooltip>
              <span className={styles.itemText}>
                {t('Docs')}
                <span className={styles.icon}>
                  <LinkIcon />
                </span>
              </span>
            </button>
          </div>
        </div>
      </div>
      <div className="main-content">
        <header>
          {bannerWorkspaceName !== null && (
            <div className={styles.sudoWrapper}>
              <div>{bannerWorkspaceName}</div>
              <div className="button">
                <button onClick={() => sudoGetOutHandle()}>
                  {t('Get out')}
                </button>
              </div>
            </div>
          )}
          <div className="page-header">
            <h2>
              <label htmlFor="nav-toggle" className={styles.menuLabel}>
                <img src={menuIcon} alt="" />
              </label>
            </h2>

            <DropdownMenu
              isOpen={isUserMenuOpen}
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              menuClassName='userMenu highlights'
              buttonName={
                <div className={styles.userWrapper}>
                  <div className={styles.userAvatar}>
                    <span>{getInitials()}</span>
                  </div>
                  <div className={styles.userInfo}>
                    <h4>
                      {currentUser.first_name} {currentUser.last_name}
                    </h4>
                    <small>
                      {currentUser.is_admin
                        ? t('Administrator')
                        : t(currentUser.type)}
                    </small>
                  </div>
                </div>
              }
              icon={<ChevronIcon />}
            >
              <ButtonsList
                itemsList={[
                    {value: 'settings', name: t('Settings'), icon: <SettingsIcon />},
                    // Only workspaces entitled to connect third-party applications
                    // over OAuth get the entry; for everyone else the page does not
                    // exist as far as the UI is concerned.
                    ...(workspaceRoles?.includes('oauth:access')
                      ? [{
                          value: 'connected-applications',
                          name: t('Connected applications'),
                          icon: <LinkIcon />,
                        }]
                      : []),
                    {value: 'docs', name: t('Docs'), icon: <HelpIcon width={18} height={18} />},
                    {value: 'logout', name: t('Logout'), icon: <LogoutIcon />},
                  ]}
                onClick={(value) => {
                  if (value === 'settings') {
                    navigate('/users/settings');
                  } else if (value === 'connected-applications') {
                    navigate('/users/connected-applications');
                  } else if (value === 'docs') {
                    window.open('https://docs.osavul.cloud/', '_blank', 'noopener,noreferrer');
                    ampli.track({
                      event_type: 'Open User Documentation',
                      event_properties: {
                        user_id: currentUser?.id,
                        workspace_id: currentUser?.workspace_id,
                      }
                    })
                  } else if (value === 'logout') {
                    logoutHandler();
                  }
                  setIsUserMenuOpen(false);
                  
                }}
              />
            </DropdownMenu>
          </div>
        </header>
      </div>

      <main className={workspaceName !== null ? 'sudo' : ''}>
        {maintenanceMessage &&
        !JSON.parse(sessionStorage.getItem('hideMaintenanceMessage')) ? (
          <MaintenanceMessage
            hideKey="hideMaintenanceMessage"
            setHideFlag={setMaintenanceMessage}
            message={maintenanceMessage}
            type='info'
          />
        ) : (
          ''
        )}

        {isAiQuotaLow &&
        !JSON.parse(sessionStorage.getItem('hideLowAIQuotaMessage')) ? (
          <MaintenanceMessage
            hideKey="hideLowAIQuotaMessage"
            setHideFlag={setIsAiQuotaLow}
            message={t(
              `Your workspace's AI quota is running low. Contact your manager to maintain access to AI-powered features.`,
            )}
            type="warning"
            actions={quotaLink}
          />
        ) : (
          ''
        )}

        {isAIQuotaInsufficient &&
        !JSON.parse(
          sessionStorage.getItem('hideAIQuotaInsufficientMessage'),
        ) ? (
          <MaintenanceMessage
            hideKey="hideAIQuotaInsufficientMessage"
            setHideFlag={setIsAIQuotaInsufficient}
            message={t(
              `Your workspace has reached its AI quota limit. Contact your manager to request more quota or wait till the next billing month.`,
            )}
            type="critical"
            actions={quotaLink}
          />
        ) : (
          ''
        )}

        {isAnalyticalQuotaLow &&
        !JSON.parse(sessionStorage.getItem('hideLowAnalyticalQuotaMessage')) ? (
          <MaintenanceMessage
            hideKey="hideLowAnalyticalQuotaMessage"
            setHideFlag={setIsAnalyticalQuotaLow}
            message={t(
              `Your workspace's Analytical quota is running low. Contact your manager to maintain access to analytical features.`,
            )}
            type="warning"
            actions={quotaLink}
          />
        ) : (
          ''
        )}

        {isAnalyticalQuotaInsufficient &&
        !JSON.parse(
          sessionStorage.getItem('hideAnalyticalQuotaInsufficientMessage'),
        ) ? (
          <MaintenanceMessage
            hideKey="hideAnalyticalQuotaInsufficientMessage"
            setHideFlag={setIsAnalyticalQuotaInsufficient}
            message={t(
              `Your workspace has reached its Analytical quota limit. Contact your manager to request more quota or wait till the next billing month.`,
            )}
            type="critical"
            actions={quotaLink}
          />
        ) : (
          ''
        )}

        {isTrialEnding &&
        !JSON.parse(sessionStorage.getItem('hideTrialEndingMessage')) ? (
          <MaintenanceMessage
            hideKey="hideTrialEndingMessage"
            setHideFlag={setIsTrialEnding}
            message={
              <>
                {t('trialPeriodEnds', { count: calculateTrialDaysLeft() })}{' '}
                {t(
                  'Contact your manager to discuss the next steps and ensure uninterrupted access.',
                )}
              </>
            }
            type="warning"
            actions={[
              <Link
                to="/users/settings#billing"
                target="_blank"
                rel="noreferrer"
              >
                {t('Check subscription')}
              </Link>,
            ]}
          />
        ) : (
          ''
        )}

        {show2FABanner && !currentUser?.otp_2fa_enabled && new Date() < TWO_FA_MANDATORY_DATE && (
          <MaintenanceMessage
            hideKey="hide2FABanner"
            setHideFlag={setShow2FABanner}
            message={t('twoFaRequiredIn', {
              count: Math.max(1, Math.ceil((TWO_FA_MANDATORY_DATE - new Date()) / (1000 * 60 * 60 * 24))),
            })}
            type="warning"
            closable={false}
            actions={[
              <button
                key="setup-2fa"
                type="button"
                className={styles.bannerLinkButton}
                onClick={() => setShow2FAModal(true)}
              >
                {t('Enable now')}
              </button>,
            ]}
          />
        )}

        <Outlet />
        <CopilotButton />
        <ToastContainer />
      </main>

      <Mandatory2FAModal
        isVisible={show2FAModal}
        onClose={() => setShow2FAModal(false)}
        onEnabled={() => {
          API.fetch('GET', '/API/v1/users/me')
            .then((data) => {
              setCurrentUser(data);
              setShow2FAModal(false);
              setShow2FABanner(false);
            })
            .catch((error) => showRequestError(error, t('User')));
        }}
        onLater={() => {
          localStorage.setItem('2fa_modal_dismissed', 'true');
          setShow2FAModal(false);
          setShow2FABanner(true);
        }}
      />
    </div>
  );
}
